import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { merge } from 'lodash';
import { BiDuplicate } from 'react-icons/bi';
import { TbArchive, TbArchiveOff, TbLock, TbLockOpen, TbStarFilled, TbStarOff } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { reportComponentService } from '@/services';
import { normalizeReportComponentConfiguration } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/configuration';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { getWorkspaceResourceTypeLabel } from '@/shared/components/workspace-resources';
import {
    appendEntity,
    createSingleFlightKey,
    removeEntityById,
    replaceEntityById,
    replaceOptimisticEntityById,
    runSingleFlight,
    upsertEntityById,
} from '@/stores/helpers/collections';
import { reduceEntityDelta } from '@/stores/helpers/delta';
import { ReportComponent } from '@/types';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';
import { ReportComponentConfigSchemaMap } from '@/types/api/modules/business-intelligence/schemas';

import { LayoutPatch, ReportComponentActions, ReportComponentState } from '../types';

let layoutSaveTimer: number | null = null;
let reportComponentsRequestSequence = 0;

const mergeReportComponent = (existing: ReportComponent | undefined, incoming: ReportComponent): ReportComponent => ({
    ...existing,
    ...incoming,
    configuration: incoming.configuration ?? existing?.configuration,
});

const mergeReportComponentList = (existing: ReportComponent[], incoming: ReportComponent[]) => {
    const existingById = new Map(existing.map((component) => [component.id, component]));

    return incoming.map((component) => mergeReportComponent(existingById.get(component.id), component));
};

const indexReportComponentsById = (components: ReportComponent[]) =>
    components.reduce<Record<string, ReportComponent>>((acc, component) => {
        acc[component.id] = component;

        return acc;
    }, {});

const mergeReportComponentById = (
    components: ReportComponent[],
    id: string,
    update: Partial<ReportComponent>,
): ReportComponent[] =>
    components.map((component) =>
        component.id === id ? mergeReportComponent(component, { ...component, ...update }) : component,
    );

const applyReportComponentUpdate = (target: ReportComponent, update: Partial<ReportComponent>) => {
    const existingConfiguration = target.configuration;

    Object.assign(target, update);

    if (update.configuration === undefined || update.configuration === null) {
        target.configuration = existingConfiguration;
    }
};

export const createReportComponentActions: StateCreator<
    ReportComponentState & ReportComponentActions,
    [['zustand/immer', never]],
    [],
    ReportComponentActions
> = (set, get, store) => ({
    // Core
    fetchReportComponents: async (params) => {
        set({ loading: true });

        try {
            const components = await reportComponentService.list(params);

            const reportComponents = mergeReportComponentList(get().reportComponents, components);

            set({
                reportComponents,
                reportComponentsById: indexReportComponentsById(reportComponents),
                loading: false,
                error: undefined,
            });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load reportComponents' });
        }
    },
    fetchReportComponentById: async (id) => {
        set((state) => {
            state.loadingReportComponent = true;
            state.error = undefined;
        });

        try {
            const reportComponent = await reportComponentService.getById(id);

            set({
                selectedReportComponent: mergeReportComponent(
                    get().selectedReportComponent ?? undefined,
                    reportComponent,
                ),
                reportComponentsById: {
                    ...get().reportComponentsById,
                    [id]: mergeReportComponent(get().reportComponentsById[id], reportComponent),
                },
                loadingReportComponent: false,
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch reportComponent detail';
                state.loadingReportComponent = false;
            });
        }
    },
    clearSelectedReportComponent: () => {
        set((state) => {
            state.selectedReportComponent = null;
        });
    },
    createReportComponent: async (input) =>
        runSingleFlight(createSingleFlightKey('reportComponents:create', input), async () => {
            const tempId = `temp-${Date.now()}`;
            const optimistic = { id: tempId, ...input } as ReportComponent;

            set({
                reportComponents: appendEntity(get().reportComponents, optimistic),
                reportComponentsById: {
                    ...get().reportComponentsById,
                    [tempId]: optimistic,
                },
            });

            try {
                const reportComponent = await reportComponentService.create(input);
                const reportComponents = replaceOptimisticEntityById(get().reportComponents, tempId, reportComponent);
                const reportComponentsById = { ...get().reportComponentsById };

                delete reportComponentsById[tempId];

                set({
                    reportComponents,
                    reportComponentsById: {
                        ...reportComponentsById,
                        [reportComponent.id]: mergeReportComponent(
                            reportComponentsById[reportComponent.id],
                            reportComponent,
                        ),
                    },
                });

                return reportComponent;
            } catch (error) {
                captureException(error);

                const reportComponentsById = { ...get().reportComponentsById };

                delete reportComponentsById[tempId];

                set({
                    reportComponents: removeEntityById(get().reportComponents, tempId),
                    reportComponentsById,
                    error: 'Failed to create reportComponent',
                });
            }
        }),
    updateReportComponent: async (id, updates) => {
        const prev = get().reportComponents.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set({
            reportComponents: mergeReportComponentById(get().reportComponents, id, updates),
            reportComponentsById: get().reportComponentsById[id]
                ? {
                      ...get().reportComponentsById,
                      [id]: mergeReportComponent(get().reportComponentsById[id], {
                          ...get().reportComponentsById[id],
                          ...updates,
                      }),
                  }
                : get().reportComponentsById,
            selectedReportComponent:
                get().selectedReportComponent?.id === id
                    ? mergeReportComponent(get().selectedReportComponent!, {
                          ...get().selectedReportComponent!,
                          ...updates,
                      })
                    : get().selectedReportComponent,
        });

        try {
            const reportComponent = await reportComponentService.update(id, updates);

            set({
                reportComponents: mergeReportComponentById(get().reportComponents, id, reportComponent),
                reportComponentsById: get().reportComponentsById[id]
                    ? {
                          ...get().reportComponentsById,
                          [id]: mergeReportComponent(get().reportComponentsById[id], reportComponent),
                      }
                    : get().reportComponentsById,
                selectedReportComponent:
                    get().selectedReportComponent?.id === id
                        ? mergeReportComponent(get().selectedReportComponent!, reportComponent)
                        : get().selectedReportComponent,
            });
        } catch (error) {
            captureException(error);

            set({
                reportComponents: replaceEntityById(get().reportComponents, id, prev),
                selectedReportComponent:
                    get().selectedReportComponent?.id === id ? prev : get().selectedReportComponent,
                error: 'Failed to update reportComponent',
            });
        }
    },
    deleteReportComponent: async (id) => {
        const prev = get().reportComponents;

        set({
            reportComponents: removeEntityById(get().reportComponents, id),
            selectedReportComponent: get().selectedReportComponent?.id === id ? null : get().selectedReportComponent,
        });

        try {
            await reportComponentService.delete(id);
        } catch (error) {
            captureException(error);

            set({ reportComponents: prev, error: 'Failed to delete reportComponent' });
        }
    },
    applyReportComponentDelta: (packet) => {
        if (packet.entity !== 'reportComponent') {
            return;
        }

        const next = reduceEntityDelta<ReportComponent>(get().reportComponents, get().selectedReportComponent, packet);

        if (!next) {
            return;
        }

        set({
            reportComponents: next.items,
            reportComponentsById: indexReportComponentsById(next.items),
            selectedReportComponent: next.selected,
        });
    },
    // - Aliases
    fetchComponentsByReportId: async (reportId: string) => {
        const requestSequence = ++reportComponentsRequestSequence;

        set((state) => {
            state.loading = true;
            state.error = undefined;
            state.reportComponentsLoadState = { reportId, status: 'loading' };
        });

        try {
            const components = await reportComponentService.listByReport(reportId);

            if (requestSequence !== reportComponentsRequestSequence) {
                return;
            }

            const reportComponents = mergeReportComponentList(get().reportComponents, components);

            set({
                reportComponents,
                reportComponentsById: indexReportComponentsById(reportComponents),
                loading: false,
                reportComponentsLoadState: { reportId, status: 'success' },
            });
        } catch (error) {
            captureException(error);

            if (requestSequence !== reportComponentsRequestSequence) {
                return;
            }

            set((state) => {
                state.loading = false;
                state.error = 'Failed to load report components';
                state.reportComponentsLoadState = { reportId, status: 'error' };
            });
        }
    },
    resetReportComponentsLoadState: () => {
        reportComponentsRequestSequence += 1;
        set({ loading: false, reportComponentsLoadState: { status: 'idle' } });
    },
    duplicateReportComponent: async (id) => {
        const prev = get().reportComponents;
        const resourceTypeLabel = getWorkspaceResourceTypeLabel('report-component');

        if (!prev) {
            return;
        }

        try {
            const duplicated = await reportComponentService.duplicate(id);
            const nextReportComponents = upsertEntityById(get().reportComponents, duplicated, { prepend: true });

            set({
                reportComponents: nextReportComponents,
                reportComponentsById: {
                    ...get().reportComponentsById,
                    [duplicated.id]: mergeReportComponent(get().reportComponentsById[duplicated.id], duplicated),
                },
                lastPersistedLayout: {
                    ...get().lastPersistedLayout,
                    [duplicated.id]: {
                        id: duplicated.id,
                        tileX: duplicated.tileX as number,
                        tileY: duplicated.tileY as number,
                        tileWidth: duplicated.tileWidth as number,
                        tileHeight: duplicated.tileHeight as number,
                    },
                },
            });

            toast(`${resourceTypeLabel} Duplicated`, {
                icon: createElement(BiDuplicate, { size: '1rem' }),
                description: duplicated.name,
                resource: {
                    actionLabel: 'View Report',
                    id: duplicated.report?.id ?? duplicated.id,
                    parentId: duplicated.report?.dashboard?.id,
                    type: 'report',
                },
            });

            return duplicated;
        } catch (error) {
            captureException(error);

            set({ reportComponents: prev, error: 'Failed to duplicate reportComponent' });

            toast.error(`Unable to Duplicate ${resourceTypeLabel}`);
        }
    },
    toggleReportComponentFavorite: async (id, favorited) => {
        const prev = get().reportComponents.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const reportComponent = state.reportComponents.find((f) => f.id === id);
            if (reportComponent) {
                reportComponent.favorited = favorited;
            }

            // Also update selectedReportComponent if it matches
            if (state.selectedReportComponent?.id === id) {
                state.selectedReportComponent.favorited = favorited;
            }
        });

        try {
            await reportComponentService.setFavorite(id, favorited);

            toast(`${favorited ? 'Added to' : 'Removed from'} Favorites`, {
                icon: createElement(favorited ? TbStarFilled : TbStarOff, { size: '1rem' }),
                description: prev.name,
                resource: {
                    actionLabel: 'View Report',
                    id: prev.report?.id ?? id,
                    parentId: prev.report?.dashboard?.id,
                    type: 'report',
                },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const reportComponent = state.reportComponents.find((f) => f.id === id);
                if (reportComponent) {
                    reportComponent.favorited = prev.favorited;
                }

                // Revert selectedReportComponent favorited if it matches
                if (state.selectedReportComponent?.id === id) {
                    state.selectedReportComponent.favorited = prev.favorited;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleReportComponentLock: async (id, locked) => {
        const prev = get().reportComponents.find((f) => f.id === id);
        const resourceTypeLabel = getWorkspaceResourceTypeLabel('report-component');

        if (!prev) {
            return;
        }

        set((state) => {
            const reportComponent = state.reportComponents.find((f) => f.id === id);
            if (reportComponent) {
                reportComponent.locked = locked;
            }

            // Also update selectedReportComponent if it matches
            if (state.selectedReportComponent?.id === id) {
                state.selectedReportComponent.locked = locked;
            }
        });

        try {
            await reportComponentService.setLocked(id, locked);

            toast(`${resourceTypeLabel} ${locked ? 'Locked' : 'Unlocked'}`, {
                icon: createElement(locked ? TbLock : TbLockOpen, { size: '1rem' }),
                description: prev.name,
                resource: {
                    actionLabel: 'View Report',
                    id: prev.report?.id ?? id,
                    parentId: prev.report?.dashboard?.id,
                    type: 'report',
                },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const reportComponent = state.reportComponents.find((f) => f.id === id);
                if (reportComponent) {
                    reportComponent.locked = prev.locked;
                }

                // Revert selectedReportComponent locked if it matches
                if (state.selectedReportComponent?.id === id) {
                    state.selectedReportComponent.locked = prev.locked;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleReportComponentArchive: async (id, archived) => {
        const prev = get().reportComponents.find((f) => f.id === id);
        const resourceTypeLabel = getWorkspaceResourceTypeLabel('report-component');

        if (!prev) {
            return;
        }

        set((state) => {
            const reportComponent = state.reportComponents.find((f) => f.id === id);
            if (reportComponent) {
                reportComponent.archived = archived;
            }

            // Also update selectedReportComponent if it matches
            if (state.selectedReportComponent?.id === id) {
                state.selectedReportComponent.archived = archived;
            }
        });

        try {
            await reportComponentService.setArchived(id, archived);

            toast(`${resourceTypeLabel} ${archived ? 'Archived' : 'Unarchived'}`, {
                icon: createElement(archived ? TbArchive : TbArchiveOff, { size: '1rem' }),
                description: prev.name,
                resource: {
                    actionLabel: 'View Report',
                    id: prev.report?.id ?? id,
                    parentId: prev.report?.dashboard?.id,
                    type: 'report',
                },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const reportComponent = state.reportComponents.find((f) => f.id === id);
                if (reportComponent) {
                    reportComponent.archived = prev.archived;
                }

                // Revert selectedReportComponent archived if it matches
                if (state.selectedReportComponent?.id === id) {
                    state.selectedReportComponent.archived = prev.archived;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    // For RGL Layout
    applyLayoutPatch: (patches) => {
        set((state) => {
            patches.forEach((p) => {
                const c = state.reportComponents.find((rc) => rc.id === p.id);

                if (!c || c.locked) {
                    return;
                } // 🚨 Lock-aware layouts

                c.tileX = p.tileX;
                c.tileY = p.tileY;
                c.tileWidth = p.tileWidth;
                c.tileHeight = p.tileHeight;
            });
        });
    },
    persistLayoutDebounced: (reportId) => {
        if (layoutSaveTimer) {
            clearTimeout(layoutSaveTimer);
        }

        layoutSaveTimer = window.setTimeout(async () => {
            const state = get();
            const patches: LayoutPatch[] = [];

            state.reportComponents.forEach((c) => {
                const last = state.lastPersistedLayout?.[c.id];
                if (
                    c.locked || // 🚨 Skip locked
                    (last &&
                        last.tileX === c.tileX &&
                        last.tileY === c.tileY &&
                        last.tileWidth === c.tileWidth &&
                        last.tileHeight === c.tileHeight)
                ) {
                    return; // No changes
                }

                patches.push({
                    id: c.id,
                    tileX: c.tileX as number,
                    tileY: c.tileY as number,
                    tileWidth: c.tileWidth as number,
                    tileHeight: c.tileHeight as number,
                });
            });

            if (patches.length === 0) {
                return;
            }

            try {
                await reportComponentService.updateLayout(reportId, patches);

                // Update snapshot
                const snapshot: Record<string, LayoutPatch> = {};

                get().reportComponents.forEach((c) => {
                    snapshot[c.id] = {
                        id: c.id,
                        tileX: c.tileX as number,
                        tileY: c.tileY as number,
                        tileWidth: c.tileWidth as number,
                        tileHeight: c.tileHeight as number,
                    };
                });
                set({ lastPersistedLayout: snapshot });
            } catch (err) {
                captureException(err);
            }
        }, 300);
    },
    // For Editing Component Configuration
    startEditingComponent: (id) => {
        const component = get().reportComponents.find((c) => c.id === id);

        if (!component) {
            return;
        }

        set({
            editingComponentId: id,
            draftProperties: {
                ...get().draftProperties,
                [id]: {
                    type: component.type,
                    embedURL: component.embedURL,
                },
            },
            draftConfigurations: {
                ...get().draftConfigurations,
                [id]: normalizeReportComponentConfiguration(component.configuration, component.type),
            },
        });
    },
    // startEditingComponent: (id) => {
    //     const component = get().reportComponentsById[id];

    //     if (!component) return;

    //     set((state) => ({
    //         editingComponentId: id,
    //         draftConfigurations: {
    //             ...state.draftConfigurations,
    //             [id]: structuredClone(component.configuration),
    //         },
    //     }));
    // },
    updateDraftConfiguration: (id, patch) => {
        set({
            draftConfigurations: {
                ...get().draftConfigurations,
                [id]: merge({}, get().draftConfigurations[id], patch),
            },
        });
    },
    replaceDraftConfiguration: (id, next) => {
        set({
            draftConfigurations: {
                ...get().draftConfigurations,
                [id]: structuredClone(next),
            },
        });
    },
    updateDraftProperties: (id, patch) => {
        set({
            draftProperties: {
                ...get().draftProperties,
                [id]: {
                    ...get().draftProperties[id],
                    ...patch,
                },
            },
        });
    },
    discardDraftConfiguration: (id) => {
        const { [id]: _discarded, ...draftConfigurations } = get().draftConfigurations;
        const { [id]: _discardedProperties, ...draftProperties } = get().draftProperties;

        set({
            draftConfigurations,
            draftProperties,
            editingComponentId: get().editingComponentId === id ? undefined : get().editingComponentId,
        });
    },
    commitDraftConfiguration: async (id) => {
        const { reportComponents, draftConfigurations, draftProperties } = get();

        // const component = reportComponentsById[id];
        const component = reportComponents?.find((rc) => rc?.id === id);

        const draft = draftConfigurations[id];
        const propertyDraft = draftProperties[id];

        if (!component || !draft || !propertyDraft) {
            return;
        }

        const nextType = propertyDraft.type ?? component.type;
        const schema = ReportComponentConfigSchemaMap[nextType];

        const versionedMerged = normalizeReportComponentConfiguration(
            {
                ...draft,
                type: nextType,
                version: 1,
            },
            nextType,
        );

        // console.log(versionedMerged);

        const result = schema?.safeParse(versionedMerged);

        if (result && !result.success) {
            throw new Error('Invalid component configuration');
        }

        const persistedConfiguration = normalizeReportComponentConfiguration(
            (result?.data ?? versionedMerged) as ReportComponentConfig,
            nextType,
        );

        const { [id]: _discarded, ...nextDraftConfigurations } = get().draftConfigurations;
        const { [id]: _discardedProperties, ...nextDraftProperties } = get().draftProperties;
        const previousState = {
            reportComponents: get().reportComponents,
            reportComponentsById: get().reportComponentsById,
            selectedReportComponent: get().selectedReportComponent,
            draftConfigurations: get().draftConfigurations,
            draftProperties: get().draftProperties,
            editingComponentId: get().editingComponentId,
        };
        const optimisticComponent = mergeReportComponent(component, {
            ...component,
            ...propertyDraft,
            type: nextType,
            configuration: persistedConfiguration as any,
        });

        set({
            reportComponents: mergeReportComponentById(get().reportComponents, id, {
                ...propertyDraft,
                type: nextType,
                configuration: persistedConfiguration as any,
            }),
            reportComponentsById: {
                ...get().reportComponentsById,
                [id]: mergeReportComponent(get().reportComponentsById[id] ?? component, optimisticComponent),
            },
            selectedReportComponent:
                get().selectedReportComponent?.id === id
                    ? mergeReportComponent(get().selectedReportComponent ?? undefined, optimisticComponent)
                    : get().selectedReportComponent,
            draftConfigurations: nextDraftConfigurations,
            draftProperties: nextDraftProperties,
            editingComponentId: undefined,
        });

        try {
            const savedComponent = await reportComponentService.update(id, {
                ...propertyDraft,
                type: nextType,
                configuration: persistedConfiguration as ReportComponentConfig,
            });

            if (!savedComponent) {
                return;
            }

            const savedConfiguration = savedComponent.configuration
                ? normalizeReportComponentConfiguration(savedComponent.configuration, savedComponent.type)
                : persistedConfiguration;
            const normalizedSavedComponent = {
                ...savedComponent,
                configuration: savedConfiguration as any,
            };

            set({
                reportComponents: mergeReportComponentById(get().reportComponents, id, normalizedSavedComponent),
                reportComponentsById: {
                    ...get().reportComponentsById,
                    [id]: mergeReportComponent(get().reportComponentsById[id] ?? component, normalizedSavedComponent),
                },
                selectedReportComponent:
                    get().selectedReportComponent?.id === id
                        ? mergeReportComponent(get().selectedReportComponent ?? undefined, normalizedSavedComponent)
                        : get().selectedReportComponent,
            });
        } catch (error) {
            set(previousState);
            throw error;
        }
    },
});
